/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ActionModel from 'cio/models/action';
import CampaignModel, { AudienceSettingsType } from 'cio/models/campaign';
import { inject as service } from '@ember/service';
import { deserializeFilters as deserializeEventFilters } from 'cio/utils/conditions/deserialize/event';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';
import CustomFormsService from 'cio/services/custom-forms';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { FormType } from 'cio/utils/forms';
import { action } from '@ember/object';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { isEventTargetInWormhole } from 'cio/utils/wormhole';
import { formTypeIcon, formTypeLabel } from 'cio/utils/forms';
import { getAudienceSummaryText } from 'cio/utils/campaigns/sumaries';

interface VisualWorkflowBuilderTriggerComponentArgs {
  isEditing: boolean;
  action: ActionModel;
  campaign: CampaignModel;
  lastIndex?: boolean;
  hasReadOnlyPermission: boolean;
}

export default class VisualWorkflowBuilderTriggerComponent extends Component<VisualWorkflowBuilderTriggerComponentArgs> {
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;

  constructor(owner: unknown, args: VisualWorkflowBuilderTriggerComponentArgs) {
    super(owner, args);

    if (args.campaign.isFormTriggered) {
      this.facebookLeadAds.initialize();
      this.customForms.initialize();
    }
  }

  get normalizedFilters() {
    if (this.args.campaign.isEventTriggered) {
      return deserializeEventFilters(this.args.campaign.attributeFilters, this.args.campaign.event);
    }

    if (this.args.campaign.isFormTriggered) {
      return deserializeAndNormalizeFormAttributeFilters(this.args.campaign.attributeFilters).children;
    }

    return null;
  }

  get attributeFiltersCount() {
    if (this.args.campaign.isEventTriggered || this.args.campaign.isFormTriggered) {
      return this.normalizedFilters?.length ?? 0;
    }

    return 0;
  }

  get formDetail() {
    if (!this.args.campaign.isFormTriggered) {
      return;
    }

    if ((this.normalizedFilters?.length ?? 0) !== 1) {
      return;
    }

    let condition = this.normalizedFilters?.[0]?.children[0] as FormEventAttributeCondition;

    if (condition == null) {
      return;
    }

    let { formType, value: id } = condition;

    let name = '';
    let icon = formTypeIcon(formType);

    if (formType === FormType.FacebookLeadAds) {
      let form = this.facebookLeadAds.forms.findBy('formId', id);
      name = form?.name ?? `Facebook Lead Ads form with Facebook Form ID of ${id}`;
    } else {
      let form = this.customForms.forms.findBy('formIdLowercase', id);
      name = form?.name ?? `${formTypeLabel(formType)} form with ID of ${id}`;
    }

    return { name, icon };
  }

  get filtersCount() {
    let { isObjectTriggered, isObjectRelationshipTriggered, filters, objectFilters, relationshipFilters } =
      this.args.campaign;
    if (isObjectTriggered || isObjectRelationshipTriggered) {
      return (filters?.length ?? 0) + (objectFilters?.length ?? 0) + (relationshipFilters?.length ?? 0);
    }
    return filters.length;
  }

  get audienceSummary() {
    return getAudienceSummaryText(this.args.campaign, this.args.campaign.objectTypeForCampaign, true);
  }

  get showAudienceConditions() {
    return this.args.campaign.audience?.type === AudienceSettingsType.filtered;
  }

  @action
  onMouseDown(event: MouseEvent) {
    // The VWB listens to mousedown events to start highlighting multiple items.
    // We don't want a mousedown from within the popover to trigger that, so we
    // stop propagation here
    event.stopPropagation();
  }

  @action
  onDropdownOpen() {
    // Clicking the trigger to open the dropdown adds focus to the trigger
    // element. If we were now to press spacebar - for example to use the
    // click-and-drag-to-scroll shortcut - it would trigger the popup again.
    // We remove the focus so that the spacebar shortcut can be used safely
    if (document.activeElement != null) {
      (document.activeElement as HTMLElement).blur?.();
    }
  }

  @action
  onDropdownClose(_dropdown: BasicDropdown, event: MouseEvent) {
    // Don't close if click was within wormhole - this means a click may have
    // happened in a nested dropdown, or a modal
    if (isEventTargetInWormhole(event)) {
      return false;
    }

    return;
  }
}
